import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import React, { forwardRef, useEffect, useMemo, useRef, useState } from "react";
import html2canvas from "html2canvas";
import { printTicketCanvases, waitForTicketImages } from "./print-window";
import { Spinner } from "react-bootstrap";
import PreviewTicketPdf from "./a4-prints/index";
import { getPrintingCount } from "Redux/middlwares/suppliers";
import { useAppDispatch } from "Redux/hooks";

const TicketPdfPreview = forwardRef(
  (
    {
      printWindow,
      setTicketLoader,
      ticketLoader,
      onHide,
      rebars,
      orderNo,
      selectedRowCount,
      showStandardLogo,
    }: any,
    ref
  ) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation("common");

    const pageSize = 1210;
    const [rebarsRefs, setRebarsRef] = useState();
    const printStarted = useRef<Window | null>(null);

    const totalPages = useMemo(() => {
      let total = {};
      if (rebarsRefs && (rebarsRefs as any).current)
        total = {
          rebars: (rebarsRefs as any).current.length,
        };

      return total;
    }, [rebarsRefs]);

    useEffect(() => {
      if (
        rebarsRefs &&
        (rebarsRefs as any).current &&
        (rebarsRefs as any).current.length > 0
      ) {
        setTicketLoader(true);
        const timer = setTimeout(() => {
          openPrintCanvas();
        }, 100);
        return () => clearTimeout(timer);
      }
    }, [rebarsRefs, printWindow]);

const getHtmlToCanvas = async (element: HTMLElement) => {
  await waitForTicketImages(element);
  if (Array.from(element.querySelectorAll("img")).some(image => !image.complete || !image.naturalWidth)) {
    throw new Error("Ticket image failed to load");
  }
  if (element.querySelector("[data-ticket-overflow=unresolved]")) {
    throw new Error("Ticket content does not fit without changing the existing design");
  }

  return html2canvas(element, {
    allowTaint: false,
    useCORS: true,
    logging: true,
    backgroundColor: "#ffffff",
    imageTimeout: 15000,
  });
};

    async function openPrintCanvas() {
      if (printWindow && printStarted.current === printWindow) return;
      printStarted.current = printWindow;
      try {
        if (!printWindow || printWindow.closed) throw new Error("Ticket print window unavailable");
        const canvases = await generateCanvases();
        await printTicketCanvases(printWindow, canvases);
        dispatch(getPrintingCount());
        onHide();
      } catch (err) {
        if (printWindow && !printWindow.closed) printWindow.close();
        toast.error(err instanceof Error && err.message === "Ticket content does not fit without changing the existing design"
          ? t("Ticket content does not fit") : t("print failed", { ns: "ticketCreator" }));
        onHide();
      } finally {
        setTicketLoader(false);
      }
    }

    async function generateCanvases() {
      const canvases = [];
      if (rebarsRefs) {
        // @ts-ignore: Object is possibly
        for (let i = 0; i < rebarsRefs.current.length; i++) {
          // @ts-ignore: Object is possibly
          const canvas = await getHtmlToCanvas(rebarsRefs.current[i]);
          canvases.push(canvas);
        }
      }
      return canvases;
    }

    // handlers
    const handleRebarsRefs = (refs: any) => {
      setRebarsRef(refs);
    };

    return (
      <>
        <div
          id="PDF-Preview-main"
          style={
            ticketLoader
              ? {
                  pointerEvents: "none",
                }
              : {}
          }
        >
          <PreviewTicketPdf
            pageSize={pageSize}
            loader={ticketLoader}
            rebars={rebars}
            orderNo={orderNo}
            handleRefs={handleRebarsRefs}
            totalPages={totalPages}
            selectedRowCount={selectedRowCount}
            showStandardLogo={showStandardLogo}
          />
        </div>
        {ticketLoader && (
          <div
            className="overloaded-wrapper"
            style={{ position: "absolute", zIndex: "12" }}
          >
            <div className="overloaded-spinner">
              <Spinner animation="border" role="status">
                <span className="visually-hidden"></span>
              </Spinner>
              <p>Generating PDF</p>
            </div>
          </div>
        )}
      </>
    );
  }
);

export default TicketPdfPreview;
